import PropTypes from 'prop-types';
import { useRef, useEffect, memo } from 'react';
import { useModalOverlay, Overlay, DismissButton } from 'react-aria';
import { useTranslation } from 'react-i18next';

/** PropTypes */
import { colorPropTypes } from '../propTypes';

/** Hooks */
import { useCloseWithTransition } from '../hooks/useCloseWithTransition';

/** Components */
import Button from './Button';
import Icon from './Icon';

/**
 * <Modal />
 */

const Modal = ({ state: modalState, className, children, color, ...props }) => {
  const { t: __ } = useTranslation();

  const close = () => (props.onClose ? props.onClose() : modalState.close());

  const underlayRef = useRef();
  const overlayRef = useRef();
  const { overlayProps, underlayProps } = useModalOverlay(props, modalState, overlayRef);
  const closeWithTransition = useCloseWithTransition(underlayRef, close, 250);

  /** Close the overlay when pressing the Escape key */
  useEffect(() => {
    const keyPressHandler = (e) => e.key === 'Escape' && closeWithTransition();
    window.addEventListener('keyup', keyPressHandler);
    return () => window.removeEventListener('keyup', keyPressHandler);
  }, [closeWithTransition]);

  /** Props */
  const ariaProps = { ...(props.id && { id: props.id }) };

  return (
    <Overlay>
      <div ref={underlayRef} {...underlayProps} {...ariaProps} className={`modal ${className}`}>
        <div>
          <div ref={overlayRef} {...overlayProps} className={`modal-content ${color ? `bg-${color}` : ''}`}>
            <Button className="modal-close-button" onPress={closeWithTransition}>
              <Icon icon="xmark" />
              <span>{__('button.Close')}</span>
            </Button>
            {children}
            <DismissButton onDismiss={close} />
          </div>
        </div>
      </div>
    </Overlay>
  );
};

Modal.propTypes = {
  /** The id of the modal */
  id: PropTypes.string,
  /** The react-aria state of the element */
  state: PropTypes.object,
  /** The class names to add to the element */
  className: PropTypes.string,
  /** The children of the element */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /** The color of the element */
  color: colorPropTypes,
  /** A callback for the onClose event */
  onClose: PropTypes.func,
};
Modal.defaultProps = {
  className: '',
};

export default memo(Modal);
